import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";

import Header from "./components/Header";
import HeroSlider from "./components/HeroSlider";
import Advantages from "./components/Advantages";
import About from "./components/About";
import Services from "./components/Services";
import Process from "./components/Process";
import People from "./components/People";
import Partners from "./components/Partners";
import ContactForm from "./components/ContactForm";
import Footer from "./components/Footer";
import AlJabriyahAssistant from "./components/AlJabriyahAssistant";
import Protected from "./components/Protected";
import LoginRedirect from "./components/LoginRedirect";
import { supabase } from "./supabase";

import ManagementConsultingPage from "./pages/ManagementConsultingPage";
import BusinessPage from "./pages/BusinessPage";
import EventPage from "./pages/EventPage";
import ITPage from "./pages/ITPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import EmailConfirmation from "./pages/EmailConfirmation";
import ResetPasswordConfirm from "./pages/ResetPasswordConfirm";
import PaymentPage from "./pages/PaymentPage";
import FeasibilityTermsPage from "./pages/FeasibilityTermsPage";
import Feasibility from "./pages/Feasibility";
import AdminCoupons from "./pages/AdminCoupons";
import ClientDashboard from "./pages/ClientDashboard";
import Marketsurvey from "./pages/Marketsurvey";
import TechnicalSurvey from "./pages/TechnicalSurvey";

import "./styles/global.css";
import "./styles/header.css";
import "./styles/hero.css";
import "./styles/advantages.css";
import "./styles/about.css";
import "./styles/services.css";
import "./styles/process.css";
import "./styles/people.css";
import "./styles/partners.css";
import "./styles/contact.css";
import "./styles/footer.css";
import "./styles/fixed-bg.css";
import "./styles/managHero.css";
import "./styles/businessHero.css";
import "./styles/eventHero.css";
import "./styles/itHero.css";
import "./styles/reset-password.css";
import "./styles/payment.css";
import "./styles/terms.css";
import "./styles/feasibility.css";
import "./styles/servicessection.css";
import "./styles/clientdashboard.css";
import "./styles/responsive.css";

function App() {
  const [language, setLanguage] = useState("en");
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "ar" : "en"));
  };

  const location = useLocation();

  useEffect(() => {
    const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""));
    const searchParams = new URLSearchParams(location.search);
    const isPasswordRecovery =
      hashParams.get("type") === "recovery" ||
      searchParams.get("type") === "recovery";

    if (isPasswordRecovery) {
      sessionStorage.setItem("passwordRecoveryInProgress", "true");

      if (location.pathname !== "/reset-password-confirm") {
        navigate(`/reset-password-confirm${location.search}${location.hash}`, {
          replace: true,
        });
      }
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        sessionStorage.setItem("passwordRecoveryInProgress", "true");

        if (window.location.pathname !== "/reset-password-confirm") {
          navigate("/reset-password-confirm", { replace: true });
        }
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, [navigate]);

  // ✅ Scroll behavior on route change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.hash]);

  return (
    <div className={`app ${language === "ar" ? "ar-active" : ""}`}>
      <Header
        language={language}
        toggleLanguage={toggleLanguage}
        isScrolled={isScrolled}
      />

      <Routes>
        <Route
          path="/"
          element={
            <main>
              <HeroSlider language={language} />
              <Advantages language={language} />
              <About language={language} />
              <div className="fixed-bg-wrapper">
                <Services language={language} />
                <Process language={language} />
                <People language={language} />
              </div>
              <Partners language={language} />
              <ContactForm language={language} />
            </main>
          }
        />
        <Route
          path="/services/management"
          element={<ManagementConsultingPage language={language} />}
        />
        <Route
          path="/services/business"
          element={<BusinessPage language={language} />}
        />
        <Route
          path="/services/event"
          element={<EventPage language={language} />}
        />
        <Route
          path="/feasibility"
          element={<Feasibility language={language} />}
        />
        <Route path="/services/it" element={<ITPage language={language} />} />
        <Route
          path="/login"
          element={
            <LoginRedirect>
              <Login />
            </LoginRedirect>
          }
        />{" "}
        <Route path="/email-confirmation" element={<EmailConfirmation />} />
        <Route path="/register" element={<Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/reset-password-confirm"
          element={<ResetPasswordConfirm />}
        />
        <Route
          path="/terms/feasibility"
          element={
            <Protected>
              <FeasibilityTermsPage language={language} />
            </Protected>
          }
        />
        <Route path="/payment" element={<PaymentPage language={language} />} />
        <Route
          path="/Marketsurvey"
          element={
            <Marketsurvey language={language} setLanguage={setLanguage} />
          }
        />
        <Route
          path="/technical-survey"
          element={
            <TechnicalSurvey language={language} setLanguage={setLanguage} />
          }
        />
        <Route
          path="/TechnicalSurvey"
          element={
            <TechnicalSurvey language={language} setLanguage={setLanguage} />
          }
        />
        {/* <Route path="/dashboard" element={<ProtectedRoute />} /> */}
        <Route
          path="/admin/coupons"
          element={
            <Protected>
              <AdminCoupons language={language} />
            </Protected>
          }
        />
        <Route
          path="/dashboard"
          element={
            <Protected>
              <ClientDashboard />
            </Protected>
          }
        />
      </Routes>

      <Footer language={language} />

      <AlJabriyahAssistant language={language} />
    </div>
  );
}

export default App;
